// Utility functions to handle distributor data from CSV

/**
 * Maps a location name to its normalized slug form
 * @param {string} locationName - Name of location (state or country)
 * @returns {string} - URL-friendly slug
 */
export const formatUrlSlug = (name) => {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with hyphens
    .replace(/á/g, 'a')             // Remove accents
    .replace(/é/g, 'e')
    .replace(/í/g, 'i')
    .replace(/ó/g, 'o')
    .replace(/ú/g, 'u')
    .replace(/ü/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-');        // Replace multiple hyphens with single hyphen
};

/**
 * Convert slug back to readable name
 * @param {string} urlSlug - URL slug
 * @returns {string} - Formatted readable name
 */
export const formatLocationName = (urlSlug) => {
  if (!urlSlug) return "Ubicación no encontrada";
  
  // Handle special cases
  const specialCases = {
    'ciudad-de-mexico': 'Ciudad de México',
    'estado-de-mexico': 'Estado de México',
    'nuevo-leon': 'Nuevo León',
    'san-luis-potosi': 'San Luis Potosí',
    'quintana-roo': 'Quintana Roo',
    'estados-unidos': 'Estados Unidos',
    'republica-dominicana': 'República Dominicana'
  };
  
  if (specialCases[urlSlug]) {
    return specialCases[urlSlug];
  }
  
  // Standard formatting
  return urlSlug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Map of Mexican states with their regions
export const mexicoStates = [
  { name: 'Aguascalientes', region: 'Centro' },
  { name: 'Baja California', region: 'Norte' },
  { name: 'Baja California Sur', region: 'Norte' },
  { name: 'Campeche', region: 'Sur' },
  { name: 'Chiapas', region: 'Sur' },
  { name: 'Chihuahua', region: 'Norte' },
  { name: 'Ciudad de México', region: 'Centro' },
  { name: 'Coahuila', region: 'Norte' },
  { name: 'Colima', region: 'Occidente' },
  { name: 'Durango', region: 'Norte' },
  { name: 'Estado de México', region: 'Centro' },
  { name: 'Guanajuato', region: 'Centro' },
  { name: 'Guerrero', region: 'Sur' },
  { name: 'Hidalgo', region: 'Centro' },
  { name: 'Jalisco', region: 'Occidente' },
  { name: 'Michoacán', region: 'Occidente' },
  { name: 'Morelos', region: 'Centro' },
  { name: 'Nayarit', region: 'Occidente' },
  { name: 'Nuevo León', region: 'Norte' },
  { name: 'Oaxaca', region: 'Sur' },
  { name: 'Puebla', region: 'Centro' },
  { name: 'Querétaro', region: 'Centro' },
  { name: 'Quintana Roo', region: 'Sur' },
  { name: 'San Luis Potosí', region: 'Centro' },
  { name: 'Sinaloa', region: 'Norte' },
  { name: 'Sonora', region: 'Norte' },
  { name: 'Tabasco', region: 'Sur' },
  { name: 'Tamaulipas', region: 'Norte' },
  { name: 'Tlaxcala', region: 'Centro' },
  { name: 'Veracruz', region: 'Sur' },
  { name: 'Yucatán', region: 'Sur' },
  { name: 'Zacatecas', region: 'Norte' }
];

// List of international countries with distributors
export const internationalCountries = [
  'Argentina', 'Bolivia', 'Chile', 'Colombia', 'Costa Rica', 
  'Ecuador', 'El Salvador', 'España', 'Estados Unidos', 
  'Honduras', 'Nicaragua', 'Panama', 'Panamá', 'Perú', 'Peru',
  'Republica Dominicana', 'República Dominicana'
];

// Parse the distributors data from raw CSV
export const parseDistributorData = (csvData) => {
  if (!csvData) return [];
  
  const lines = csvData.split('\n');
  if (lines.length <= 1) {
    console.error("[parseDistributorData] CSV data has no content or only a header row.");
    return [];
  }

  // Define a mapping for common CSV headers to desired JS object keys
  const headerMapping = {
    // Name variations (lowercase keys)
    'distribuidor': 'Name',
    'nombre': 'Name',
    'nombre del distribuidor': 'Name',
    'razon social': 'Name',
    'razón social': 'Name',
    'empresa': 'Name',
    'name': 'Name',

    // Location variations (estado/ciudad for map grouping)
    'estado': 'Location',
    'ubicacion': 'Location',
    'ubicación': 'Location',
    'ciudad': 'Location', // Could also be City, but Location is used for grouping
    'municipio': 'Location',
    'location': 'Location',

    // Address variations (full street address for display)
    'direccion': 'Address',
    'dirección': 'Address',
    'domicilio': 'Address',
    'calle y numero': 'Address',
    'address': 'Address',

    // Phone variations
    'telefono': 'Phone',
    'teléfono': 'Phone',
    'celular': 'Phone',
    'phone': 'Phone',

    // Email variations
    'correo': 'Email',
    'correo electronico': 'Email',
    'correo electrónico': 'Email',
    'email': 'Email',

    // Website variations
    'sitio web': 'Website',
    'pagina web': 'Website',
    'website': 'Website',
    'sitio': 'Website',
    'url': 'Website',

    // Contact Person
    'contacto': 'ContactPerson',
    'persona de contacto': 'ContactPerson',
    'responsable': 'ContactPerson',
    
    // Explicitly map common English terms to ensure they get canonical casing
    // if they appear in CSV as lowercase.
    'name': 'Name',
    'location': 'Location',
    'address': 'Address',
    'phone': 'Phone',
    'email': 'Email',
    'website': 'Website',
    'contactperson': 'ContactPerson'
  };

  const rawHeaders = lines[0].split(',').map(header => 
    header.replace(/^"(.*)"$/, '$1').trim().toLowerCase()
  );
  console.log("[parseDistributorData] Detected CSV Headers (raw, trimmed, lowercased):", rawHeaders);

  const finalMappedKeys = rawHeaders.map(rawHeader => {
    const mapped = headerMapping[rawHeader];
    if (mapped) return mapped;
    // Fallback: if rawHeader itself is a known target key (e.g., 'name', 'address'), use canonical form
    if (Object.values(headerMapping).map(v => v.toLowerCase()).includes(rawHeader)) {
        // Find the canonical casing (e.g. 'name' -> 'Name')
        for (const key in headerMapping) {
            if (headerMapping[key].toLowerCase() === rawHeader) return headerMapping[key];
        }
    }
    console.warn(`[parseDistributorData] Unmapped CSV header: '${rawHeader}'. This column will be ignored unless it's a direct match to a property like 'Name', 'Address', etc. in its original casing, or the frontend code is adapted.`);
    // We will use the rawHeader (as read) as a key if no mapping, hoping for direct match if needed by other code,
    // but primarily relying on the mapped keys for core fields.
    // For stricter parsing where only mapped headers are used, this could return null or a generic prefix.
    return rawHeader; // Or null to strictly ignore unmapped headers for the primary object keys
});

  const distributors = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue; 
    
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let j = 0; j < lines[i].length; j++) {
      const char = lines[i][j];
      if (char === '"') {
        inQuotes = !inQuotes;
        if ((lines[i][j-1] === ',' || j === 0) && inQuotes) continue;
        if ((lines[i][j+1] === ',' || j === lines[i].length -1) && !inQuotes && current.endsWith('"')) continue;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    if (i === 1) { // Log the first data row values for diagnostics
        console.log("[parseDistributorData] First data row raw values:", values);
    }

    const distributor = {};
    finalMappedKeys.forEach((mappedKey, index) => {
      if (mappedKey && index < values.length) { // Ensure mappedKey exists and index is within bounds
        let value = values[index] || '';
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.substring(1, value.length - 1);
        }
        
        // Special case for location field - normalize Estado de México variations
        if (mappedKey === 'Location' && 
            (value.includes('Estado de M') || 
             value.toLowerCase() === 'estado de mexico' || 
             value.toLowerCase() === 'estado de méxico')) {
          value = 'Estado de México';
        } else if (mappedKey === 'Location' && 
                  (value === 'CDMX' || 
                   value.toLowerCase() === 'ciudad de mexico' || 
                   value.toLowerCase() === 'ciudad de méxico' ||
                   value.toLowerCase() === 'df' ||
                   value.toLowerCase() === 'd.f.' ||
                   value.toLowerCase() === 'distrito federal')) {
          value = 'Ciudad de México';
        }
        
        distributor[mappedKey] = value;
      } else if (!mappedKey && index < values.length) {
        // If header wasn't mapped but we decided to keep it (e.g. finalMappedKeys returns rawHeader)
        // This part is tricky, as we don't know the intended key. For now, we assume mappedKey path handles essentials.
        // console.log(`[parseDistributorData] Storing value for unmapped header '${rawHeaders[index]}': ${values[index]}`);
        // distributor[rawHeaders[index]] = values[index] || ''; // Example of storing with original header
      }
    });
    
    const essentialKeys = ['Name', 'Address', 'Location', 'Phone', 'Email', 'Website', 'ContactPerson'];
    essentialKeys.forEach(key => {
      if (!(key in distributor)) {
        distributor[key] = '';
      }
    });

    distributors.push(distributor);
  }
  
  if (distributors.length > 0) {
    console.log("[parseDistributorData] First fully parsed distributor object:", JSON.stringify(distributors[0], null, 2));
  }
  console.log(`[parseDistributorData] Parsed a total of ${distributors.length} distributors.`);
  return distributors;
};

// Determine if a distributor is Mexican or international
export const getDistributorType = (distributor) => {
  if (!distributor || !distributor.Location) return 'unknown';
  
  // Check country marker in location or address field
  const locationAndAddress = (distributor.Location + ' ' + distributor.Address).toLowerCase();
  
  // Check for international markers
  const internationalMarkers = ['argentina', 'bolivia', 'chile', 'colombia', 'costa rica', 
    'ecuador', 'el salvador', 'españa', 'españa', 'spain', 'usa', 'estados unidos', 'united states',
    'honduras', 'nicaragua', 'panama', 'panamá', 'perú', 'peru', 'republica dominicana', 'república dominicana', 
    'dominican republic'];
  
  for (const marker of internationalMarkers) {
    if (locationAndAddress.includes(marker)) {
      return 'international';
    }
  }
  
  // Default to Mexico if no international markers found
  return 'mexico';
};

// Get the location name from a distributor 
export const getDistributorLocation = (distributor) => {
  if (!distributor || !distributor.Location) return '';
  
  // Try to extract state or country from location
  const location = distributor.Location;
  
  // Manejar específicamente casos de edomex que vienen en el CSV
  if (location === 'edomex' || 
      location.toLowerCase() === 'estado de mexico' || 
      location.toLowerCase() === 'estado de méxico') {
    return 'Estado de México';
  }
  
  // Manejar específicamente Nuevo León
  if (location.includes('Nuevo León') || 
      location.includes('Nuevo Leon') || 
      location.includes('Monterrey')) {
    return 'Nuevo León';
  }
  
  // Manejar específicamente Yucatán
  if (location.includes('Yucatán') || 
      location.includes('Yucatan') || 
      location.includes('Merida')) {
    return 'Yucatán';
  }
  
  // Caso exacto para CDMX
  if (location === 'CDMX' || 
      location === 'Ciudad de México' || 
      location === 'Ciudad de Mexico') {
    return 'Ciudad de México';
  }
  
  // Handle special cases for Ciudad de México (formerly DF)
  if (location.includes('D.F.') || 
      location === 'DF' || 
      location === 'Distrito Federal' || 
      location.includes('Mexico City')) {
    return 'Ciudad de México';
  }
  
  // Para los demás estados, buscar por nombre normalizado
  const normalizedLocation = location.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // remove accents
  
  // For Mexico, try to match states
  for (const state of mexicoStates) {
    // Saltarse CDMX y EdoMex aquí porque ya los manejamos arriba
    if (state.name === 'Ciudad de México' || state.name === 'Estado de México') {
      continue;
    }
    
    const normalizedState = state.name
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
    if (normalizedLocation.includes(normalizedState)) {
      return state.name; // Return the proper name with accents
    }
  }
  
  // For international, try to match countries
  for (const country of internationalCountries) {
    if (typeof country === 'string') {
      if (location.includes(country)) {
        return country;
      }
    } else if (country.name && location.includes(country.name)) {
      return country.name;
    }
  }
  
  return 'Desconocido';
};

// Group distributors by location (state or country)
export const groupDistributorsByLocation = (distributors) => {
  const grouped = {};
  
  distributors.forEach(distributor => {
    // La ubicación original tal como viene en el CSV
    const originalLocation = distributor.Location;
    
    if (!originalLocation) return;
    
    // Lógica especial para manejar el caso de 'edomex' explícitamente
    if (originalLocation === 'edomex') {
      if (!grouped['edomex']) {
        grouped['edomex'] = [];
      }
      grouped['edomex'].push(distributor);
      return;
    }
    
    // Buscar Yucatán exactamente como viene en el CSV
    if (originalLocation.includes('Yucatán') || originalLocation.includes('Merida')) {
      if (!grouped[originalLocation]) {
        grouped[originalLocation] = [];
      }
      grouped[originalLocation].push(distributor);
      return;
    }
    
    // Buscar Nuevo León exactamente como viene en el CSV
    if (originalLocation.includes('Nuevo León') || originalLocation.includes('Monterrey')) {
      if (!grouped[originalLocation]) {
        grouped[originalLocation] = [];
      }
      grouped[originalLocation].push(distributor);
      return;
    }
    
    // Para otros casos, intentar obtener el nombre normalizado de estado
    const location = getDistributorLocation(distributor);
    if (!location || location === 'Desconocido') return;
    
    if (!grouped[location]) {
      grouped[location] = [];
    }
    
    grouped[location].push(distributor);
  });
  
  console.log("Distribuidores agrupados:", Object.keys(grouped));
  return grouped;
}; 